import * as CActions from 'ApplicationEntry/ducks/ProductsRiders/cancer';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {isValidPolicyNumberPaperElectronic} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {matchCancerByType} from 'ApplicationEntry/models/ProductsRiders/Cancer/Cancer';
import * as C from 'ApplicationEntry/models/ProductsRiders/Cancer/OrdinaryCancer';
import {ProductRiderGroups, ProductsRidersPolicyType} from 'ApplicationEntry/models/ProductsRiders/Families';
import {CP10PolicyOption, CP12PolicyOption} from 'ApplicationEntry/models/ProductsRiders/Policyoptions';
import {isNonEmptyString} from 'Core/models/EmptyString';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import * as ErrorMessages from '../../ErrorMessages';
import './../ProductRiders.sass';

import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import VariableCodeRiderInformation from '../VariableCodeRiderInformation';
import PlanSelect from '../PlanSelect';

type CancerProps = {
  cancer: C.OrdinaryCancer;
};

const getApplicationType = matchCancerByType({
  Group: () => ApplicationType.Group,
  Ordinary: () => ApplicationType.Ordinary,
  NewYork: () => ApplicationType.NewYork,
});

const Cancer = (props: CancerProps) => {
  const dispatch = useDispatch();
  const {
    planType,
    productType,
    policyOption,
    groupTrust,
    overRideBillForm,
    originalAge,
    reRate,
    calculatedPremium,
    riderInformation,
    cwa,
    policyNumber,
    replacement,
    section125,
    productList,
  } = props.cancer;

  const [planTypeBlurred, setBlurPlanType] = useState(false);
  const [policyNumberBlurred, setPolicyNumberBlurred] = useState(false);
  const showPlanTypeError = planTypeBlurred && planType.key === 'Empty';
  const [planTypeLabel, setPlanTypeLabel] = useState<HTMLLabelElement | null>();
  const [policyOptionLabel, setPolicyOptionLabel] = useState<HTMLLabelElement | null>();

  const showPolicyNumberError =
    policyNumberBlurred && !isValidPolicyNumberPaperElectronic(policyNumber) && (reRate || replacement);

  useEffect(() => {
    if (isNonEmptyString(productType)) {
      dispatch(CActions.getCancerRiderCodes(productType.value, ProductRiderGroups[ProductsRidersPolicyType.Cancer]));
    }
  }, [dispatch, productType]);

  return (
    <div className="product-riders-section__policy-type" id="cancer-section">
      <form noValidate autoComplete="off" className="product-riders-section__policy-type__form">
        <div className="product-riders-section__policy-type__form__row">
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <PlanSelect
                productType={productType}
                productList={productList}
                family={ProductRiderGroups[ProductsRidersPolicyType.Cancer]}
                applicationType={getApplicationType(props.cancer)}
                friendlyName="ord_cancer"
                changePlanAction={CActions.changeProductType}
                getPlansAction={CActions.getCancerProducts}
              />
            </Grid>
          </Grid>
          {productType.key === 'NonEmpty' ? (
            <>
              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                    <InputLabel ref={setPolicyOptionLabel} id="cancer-policy-option-label">
                      Policy Option {productType.value === 'CP12' ? '(RAD, SURG & MISC)' : ''}
                    </InputLabel>
                    <Select
                      labelId="cancer-label-policy-option"
                      labelWidth={policyOptionLabel?.clientWidth}
                      id="cancer-policy-option"
                      className="cancer-label-policy-option"
                      value={policyOption.key === 'Empty' ? '' : policyOption.value}
                      onChange={e => {
                        dispatch(CActions.changePolicyOption(e.target.value as string));
                      }}
                    >
                      {productType.value === 'CP10'
                        ? CP10PolicyOption.map(policyOptionItem => (
                            <MenuItem value={policyOptionItem.key} key={policyOptionItem.key}>
                              {policyOptionItem.value}
                            </MenuItem>
                          ))
                        : CP12PolicyOption.map(policyOptionItem => (
                            <MenuItem value={policyOptionItem.key} key={policyOptionItem.key}>
                              {policyOptionItem.value}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl
                    error={showPlanTypeError}
                    variant="outlined"
                    className="product-riders-section__policy-type__form__row--field"
                  >
                    <InputLabel ref={setPlanTypeLabel} id="cancer-plantype-label">
                      Plan Type
                    </InputLabel>
                    <Select
                      labelId="cancer-label-plantype"
                      labelWidth={planTypeLabel?.clientWidth}
                      id="critical-illness-plantype"
                      value={planType.key === 'Empty' ? '' : planType.value}
                      onBlur={() => setBlurPlanType(true)}
                      onChange={e => {
                        dispatch(CActions.changePlanType(e.target.value as string));
                      }}
                    >
                      <MenuItem value="Individual">Individual</MenuItem>
                      <MenuItem value="Family">Family</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="cancer-group-trust"
                        checked={groupTrust}
                        onChange={() => dispatch(CActions.changeGroupTrust(!groupTrust))}
                        disabled
                      />
                    }
                    label={<Typography>Group Trust</Typography>}
                    className="product-riders-section__policy-type__form__row--field"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="cancer-replacement"
                        checked={replacement}
                        onChange={() => dispatch(CActions.changeReplacement(!replacement))}
                      />
                    }
                    label={<Typography className="case-details-section__checkbox-label">Replacement</Typography>}
                    className="product-riders-section__policy-type__form__row--field"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    id="cancer-original-age"
                    label="Original Age"
                    variant="outlined"
                    placeholder="Enter Original Age"
                    margin="normal"
                    fullWidth
                    type="number"
                    className="product-riders-section__policy-type__form__row--field"
                    value={!reRate ? '' : originalAge}
                    onChange={e => dispatch(CActions.changeOriginalAge(parseInt(e.target.value, 10)))}
                    disabled={!reRate}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="cancer-rerate"
                        checked={reRate}
                        onChange={() => dispatch(CActions.changeRerate(!reRate))}
                      />
                    }
                    label={<Typography className="case-details-section__checkbox-label">Re-rate</Typography>}
                    className="product-riders-section__policy-type__form__row--field"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="cancer-policy-number"
                    label="Policy Number"
                    variant="outlined"
                    placeholder="Enter Policy Number"
                    margin="normal"
                    fullWidth
                    error={showPolicyNumberError}
                    helperText={showPolicyNumberError ? ErrorMessages.INVALID_POLICY_NUMBER : ''}
                    className="product-riders-section__policy-type__form__row--field"
                    value={
                      policyNumber.key !== 'ValidPolicyNumber' || (!reRate && !replacement)
                        ? ''
                        : policyNumber.value.value
                    }
                    onBlur={() => setPolicyNumberBlurred(true)}
                    onChange={e => dispatch(CActions.changePolicyNumber(e.target.value as string))}
                    disabled={!reRate && !replacement}
                    inputProps={{
                      maxLength: 10,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="cancer-override-bill-form"
                        checked={overRideBillForm}
                        onChange={() => dispatch(CActions.changeOverrideBillForm(!overRideBillForm))}
                        disabled
                      />
                    }
                    label={<Typography>Override Bill Form To Direct</Typography>}
                    className="product-riders-section__policy-type__form__row--field"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="cancer-section125"
                        checked={section125}
                        onChange={() => dispatch(CActions.changeSection125(!section125))}
                      />
                    }
                    label={<Typography>Section 125</Typography>}
                    className="product-riders-section__policy-type__form__row--field"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="cancer-premium"
                    label="Premium"
                    variant="outlined"
                    placeholder="Enter Premium"
                    margin="normal"
                    fullWidth
                    className="product-riders-section__policy-type__form__row--field"
                    value={calculatedPremium.value}
                    onChange={event => {
                      dispatch(CActions.changePremium(event.target.value as string));
                      dispatch(CActions.changeCalculatedPremium(event.target.value as string));
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="cancer-cwa"
                    label="CWA"
                    variant="outlined"
                    placeholder="Enter CWA"
                    margin="normal"
                    fullWidth
                    className="product-riders-section__policy-type__form__row--field"
                    value={cwa.value}
                    onChange={e => dispatch(CActions.changeCWA(e.target.value as string))}
                  />
                </Grid>
              </Grid>

              <VariableCodeRiderInformation
                showConsolidatedRiderSection
                indexCodes
                title="Riders"
                amountLabel="Rider Amount"
                enableSuccessively
                riderInformation={riderInformation}
                changeRiderInformationAction={CActions.changeRiderInformation}
                removeRiderAction={CActions.removeRider}
                identifier="ordinary-cancer"
              />
            </>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default Cancer;
